import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, Box, Theme } from '@material-ui/core';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { colors } from '@lifesize/ux-assets';
import { media } from '@lifesize/clients.sdk';
import AudioSettings from './AudioSettings';
import VideoSettings from './VideoSettings';
import { set as updateSettings } from 'reducers/mediaSettingsSlice';
import { AudioType, MediaConfig, getConstraints } from 'utils/deviceUtils';
import { writeToLocalStorage } from 'utils/localStorage/mediaSettings';
import { WEB_CONFERENCE_DISABLED, WEB_CONFERENCE_ENABLED } from 'constants/Call';
import { setVideoMute } from '../../actions/media';

interface Props {
  extension?: string;
  isInCall?: boolean; // TODO: stop passing this around everywhere and use a hook in each component
  isJoinFlowCall?: boolean;
  isPreCall?: boolean;
  onToggleVbbSettings: Function;
}

const AudioVideoSettings = (props: Props) => {
  const { onToggleVbbSettings, extension, isInCall, isJoinFlowCall } = props;
  const classes = useStyles({});
  const dispatch = useDispatch();
  const mediaSettings = useSelector((state) => get(state, 'app.mediaSettings'));

  const handleConfigChange = useCallback(
    async (key: keyof MediaConfig, value: string) => {
      const newValues: MediaConfig = { ...mediaSettings, [key]: value };

      // mute/unmute audio with phone(pstn)/computer change
      if (key === 'audio') {
        if (value === AudioType.computer) {
          media.setAudioMute(false);
          newValues.localAudioMuted = false;
          dispatch({ type: WEB_CONFERENCE_DISABLED });
        } else {
          // 'phone'
          // RemoteStream video component will automatically mute when 'phone' is selected
          media.setAudioMute(true);
          newValues.localAudioMuted = true;
          dispatch({ type: WEB_CONFERENCE_ENABLED });
        }
      }

      if (key === 'localVideoMuted') {
        // @ts-ignore
        dispatch(setVideoMute(value));
      }

      // persist the values
      writeToLocalStorage(newValues);
      // set the global state
      dispatch(updateSettings(newValues));

      if (['speakers', 'localVideoMuted', 'localAudioMuted'].indexOf(key) > -1) return;

      const oldConstraints = getConstraints(mediaSettings);
      const newConstraints = getConstraints(newValues);
      if (!isEqual(oldConstraints, newConstraints)) {
        const newMediaSettings = { defaultConstraints: newConstraints };
        await media.configure(newMediaSettings);
      }
    },
    [mediaSettings, dispatch]
  );

  return (
    <Box className={classes.container} data-testid="in-call-settings">
      <VideoSettings
        handleConfigChange={handleConfigChange}
        isInCall={isInCall}
        onToggleVbbSettings={onToggleVbbSettings}
        isJoinFlowCall={isJoinFlowCall}
      />
      <AudioSettings
        extension={extension}
        handleConfigChange={handleConfigChange}
        isInCall={isInCall}
        isJoinFlowCall={isJoinFlowCall}
      />
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    alignItems: 'stretch', // stretch children vertically
    color: colors.squidInk,
    display: 'flex',
    height: '100%',
    maxWidth: 700, // for containment in the guest join flow
    position: 'relative',
    width: '100%',

    '& > section': {
      display: 'flex',
      flexDirection: 'column',
      flex: '0 0 auto',
      textAlign: 'left',
      width: '50%',

      '&:first-child': {
        padding: `0 ${theme.spacing(3)}px 0 0`
      },

      '&:last-child': {
        padding: `0 0 0 ${theme.spacing(3)}px`
      }
    }
  }
}));

export default AudioVideoSettings;
